import { useCallback, useEffect, useState, type FormEvent } from 'react'
import Add from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { AiModelInfo, AiModelPrice, AiModelPriceInit } from 'common-billing-server/types'
import { AI_PROVIDERS, AiProvider } from 'common-billing-server/types'
import { AI_PROVIDER_LABELS } from 'common-billing-server/ai'
import { useBillingApi } from '../../api/billing/billing-context'
import { useConfirmationDialog } from '../common/ConfirmationDialog'

type RateField =
    | 'inputMicroUsdPerMillionTokens'
    | 'outputMicroUsdPerMillionTokens'
    | 'cacheWriteMicroUsdPerMillionTokens'
    | 'cacheReadMicroUsdPerMillionTokens'

type PriceDraft = Pick<AiModelPriceInit, 'provider' | 'model' | 'effectiveAt'> & Record<RateField, string>

const RATE_FIELDS = [
    ['inputMicroUsdPerMillionTokens', 'Input micro-USD / 1M tokens'],
    ['outputMicroUsdPerMillionTokens', 'Output micro-USD / 1M tokens'],
    ['cacheWriteMicroUsdPerMillionTokens', 'Cache write micro-USD / 1M tokens'],
    ['cacheReadMicroUsdPerMillionTokens', 'Cache read micro-USD / 1M tokens'],
] as const satisfies readonly (readonly [RateField, string])[]

const emptyPrice = (): PriceDraft => ({
    provider: AiProvider.bedrockMantle,
    model: '',
    effectiveAt: new Date(),
    inputMicroUsdPerMillionTokens: '',
    outputMicroUsdPerMillionTokens: '',
    cacheWriteMicroUsdPerMillionTokens: '',
    cacheReadMicroUsdPerMillionTokens: '',
})

/** The datetime-local widget has no timezone, so both halves must agree on UTC. */
const utcInputValue = (value: Date): string => value.toISOString().slice(0, 16)
const utcFromInput = (value: string): Date => (value ? new Date(`${value}:00Z`) : new Date(NaN))

/** Rates are micro-USD per million tokens and must be whole, non-negative and present. */
const rateError = (value: string): string | undefined => {
    if (!value.trim()) return 'Required'
    const parsed = Number(value)
    return !Number.isSafeInteger(parsed) || parsed < 0 ? 'Whole non-negative number' : undefined
}

/** Mirrors the ledger price lookup so drafts seed the rate currently being charged. */
const currentPriceFor = (provider: AiProvider, model: string, prices: AiModelPrice[]): AiModelPrice | undefined =>
    prices
        .filter(
            (item) => item.provider === provider && item.model === model && item.effectiveAt.getTime() <= Date.now()
        )
        .sort((left, right) => right.effectiveAt.getTime() - left.effectiveAt.getTime())[0]

const usd = (microUsdPerMillion: string): string =>
    `$${(Number(microUsdPerMillion) / 1_000_000).toFixed(2)} / 1M tokens`

export const AiPricing = ({ models }: { models: AiModelInfo[] }) => {
    const billingApi = useBillingApi()
    const showConfirmation = useConfirmationDialog()
    const [prices, setPrices] = useState<AiModelPrice[]>([])
    const [price, setPrice] = useState<PriceDraft>(emptyPrice)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string>()

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const loadedPrices = await billingApi.listAiPrices()
            setPrices(loadedPrices)
            setError(undefined)
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : String(caught))
        } finally {
            setLoading(false)
        }
    }, [billingApi])

    useEffect(() => {
        void load()
    }, [load])

    const create = async () => {
        setLoading(true)
        try {
            await billingApi.createAiPrice({
                provider: price.provider,
                model: price.model,
                effectiveAt: price.effectiveAt,
                inputMicroUsdPerMillionTokens: Number(price.inputMicroUsdPerMillionTokens),
                outputMicroUsdPerMillionTokens: Number(price.outputMicroUsdPerMillionTokens),
                cacheWriteMicroUsdPerMillionTokens: Number(price.cacheWriteMicroUsdPerMillionTokens),
                cacheReadMicroUsdPerMillionTokens: Number(price.cacheReadMicroUsdPerMillionTokens),
            })
            setPrice(emptyPrice())
            await load()
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : String(caught))
            setLoading(false)
        }
    }

    const effectiveAtIsValid = !Number.isNaN(price.effectiveAt.getTime())
    const formIsInvalid =
        !price.model.trim() || !effectiveAtIsValid || RATE_FIELDS.some(([key]) => rateError(price[key]))
    const modelOptions = [...new Set([...models.map(({ id }) => id), ...prices.map(({ model }) => model)])]

    const seedRates = (current: PriceDraft, provider: AiProvider, model: string): PriceDraft => {
        const seed = currentPriceFor(provider, model, prices)
        return {
            ...current,
            provider,
            model,
            inputMicroUsdPerMillionTokens: seed ? String(seed.inputMicroUsdPerMillionTokens) : '',
            outputMicroUsdPerMillionTokens: seed ? String(seed.outputMicroUsdPerMillionTokens) : '',
            cacheWriteMicroUsdPerMillionTokens: seed ? String(seed.cacheWriteMicroUsdPerMillionTokens) : '',
            cacheReadMicroUsdPerMillionTokens: seed ? String(seed.cacheReadMicroUsdPerMillionTokens) : '',
        }
    }

    const selectProvider = (provider: AiProvider) => setPrice((current) => seedRates(current, provider, current.model))

    const selectModel = (model: string) => setPrice((current) => seedRates(current, current.provider, model))

    const confirmAndCreate = (event: FormEvent) => {
        event.preventDefault()
        if (formIsInvalid) return
        showConfirmation(
            () => void create(),
            `Add a price version for ${price.model} on ${AI_PROVIDER_LABELS[price.provider]}, ` +
                `effective ${price.effectiveAt.toISOString()}? ` +
                `Input ${usd(price.inputMicroUsdPerMillionTokens)}, ` +
                `output ${usd(price.outputMicroUsdPerMillionTokens)}, ` +
                `cache write ${usd(price.cacheWriteMicroUsdPerMillionTokens)}, ` +
                `cache read ${usd(price.cacheReadMicroUsdPerMillionTokens)}. ` +
                'Price versions cannot be edited or deleted; a mistake can only be superseded by a later version.',
            { ok: { text: 'Add price version' } }
        )
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
                Effective-dated AI prices
            </Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Grid component="form" container spacing={2} onSubmit={confirmAndCreate} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Autocomplete
                        disableClearable
                        options={AI_PROVIDERS}
                        value={price.provider}
                        getOptionLabel={(provider) => AI_PROVIDER_LABELS[provider]}
                        onChange={(_event, selected) => selectProvider(selected)}
                        renderInput={(params) => <TextField {...params} label="Provider" required />}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Autocomplete
                        freeSolo
                        options={modelOptions}
                        value={price.model}
                        onChange={(_event, selected) => selectModel(selected ?? '')}
                        onInputChange={(_event, input) => selectModel(input)}
                        renderInput={(params) => <TextField {...params} label="Model" required />}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        fullWidth
                        type="datetime-local"
                        label="Effective at (UTC)"
                        required
                        slotProps={{ inputLabel: { shrink: true } }}
                        helperText="Interpreted as UTC. This price version cannot be edited or deleted once created."
                        value={effectiveAtIsValid ? utcInputValue(price.effectiveAt) : ''}
                        onChange={(event) =>
                            setPrice((current) => ({ ...current, effectiveAt: utcFromInput(event.target.value) }))
                        }
                    />
                </Grid>
                {RATE_FIELDS.map(([key, label]) => (
                    <Grid key={key} size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            type="number"
                            label={label}
                            value={price[key]}
                            error={Boolean(rateError(price[key]))}
                            helperText={rateError(price[key]) ?? 'micro-USD per 1M tokens'}
                            onChange={(event) => setPrice((current) => ({ ...current, [key]: event.target.value }))}
                        />
                    </Grid>
                ))}
                <Grid size={{ xs: 12 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || formIsInvalid}
                        startIcon={loading ? <CircularProgress size={16} /> : <Add />}
                    >
                        Add price version
                    </Button>
                </Grid>
            </Grid>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Effective</TableCell>
                        <TableCell>Provider</TableCell>
                        <TableCell>Model</TableCell>
                        <TableCell align="right">Input</TableCell>
                        <TableCell align="right">Output</TableCell>
                        <TableCell align="right">Cache write</TableCell>
                        <TableCell align="right">Cache read</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {prices.map((item) => (
                        <TableRow key={item.id}>
                            <TableCell>{item.effectiveAt.toISOString()}</TableCell>
                            <TableCell>{AI_PROVIDER_LABELS[item.provider]}</TableCell>
                            <TableCell>{item.model}</TableCell>
                            <TableCell align="right">{item.inputMicroUsdPerMillionTokens}</TableCell>
                            <TableCell align="right">{item.outputMicroUsdPerMillionTokens}</TableCell>
                            <TableCell align="right">{item.cacheWriteMicroUsdPerMillionTokens}</TableCell>
                            <TableCell align="right">{item.cacheReadMicroUsdPerMillionTokens}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    )
}
